import { useState } from "react";
import { FaEnvelope, FaEye, FaEyeSlash, FaLock, FaUserPlus } from "react-icons/fa";
import axios from "../../auth/auth";

const initialForm = {
    name: "",
    email: "",
    password: "",
    role: "Sales"
};

export default function ManageEmployeesTab() {
    const [form, setForm] = useState(initialForm);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (event) => {
        setForm({ ...form, [event.target.name]: event.target.value });
        setError("");
        setSuccess("");
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setSuccess("");
        setIsSubmitting(true);

        try {
            await axios.post("/auth/signup", form);
            setForm(initialForm);
            setSuccess("Employee created successfully.");
        } catch (err) {
            setError(err.response?.data?.detail || "Unable to create employee.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="mx-auto max-w-2xl rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-6 flex items-center gap-3">
                <div className="rounded-lg bg-blue-50 p-3 text-blue-600">
                    <FaUserPlus />
                </div>
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">Manage Employees</h2>
                    <p className="text-sm text-gray-500">Create an employee account and assign a role.</p>
                </div>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700" htmlFor="employee-name">
                        Full name
                    </label>
                    <input
                        id="employee-name"
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="w-full rounded-lg border border-gray-200 px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                </div>

                <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700" htmlFor="employee-email">
                        Email address
                    </label>
                    <div className="relative">
                        <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            id="employee-email"
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                            className="w-full rounded-lg border border-gray-200 py-3 pl-11 pr-4 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                        />
                    </div>
                </div>

                <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700" htmlFor="employee-password">
                        Password
                    </label>
                    <div className="relative">
                        <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            id="employee-password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={form.password}
                            onChange={handleChange}
                            required
                            minLength="8"
                            className="w-full rounded-lg border border-gray-200 py-3 pl-11 pr-12 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                        />
                            <button
                                type="button"
                                onClick={() => setShowPassword((previous) => !previous)}
                                className="absolute inset-y-0 right-3 flex w-9 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                    </div>
                </div>

                <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700" htmlFor="employee-role">
                        Role
                    </label>
                    <select
                        id="employee-role"
                        name="role"
                        value={form.role}
                        onChange={handleChange}
                        className="w-full rounded-lg border border-gray-200 px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    >
                        <option value="Admin">Admin</option>
                        <option value="Accounts">Accounts</option>
                        <option value="Sales">Sales</option>
                    </select>
                </div>

                {error && <p className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}
                {success && <p className="rounded-lg border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</p>}

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <FaUserPlus />
                    {isSubmitting ? "Creating employee..." : "Create employee"}
                </button>
            </form>
        </section>
    );
}